import { AnimatePresence, motion } from 'framer-motion';
import { Activity, BookOpen, Database, FileText, Layers, PlayCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { usePesaParcours } from '../../stores/usePesaParcours';
import AssetOverlay from './AssetOverlay';
import LearningArea from './LearningArea';
import MobileNarratorDrawer from './MobileNarratorDrawer';
import SidebarNavigation from './SidebarNavigation';

const TABS = [
  { id: 'modules',   label: 'Modules',   icon: Layers   },
  { id: 'contenu',   label: 'Contenu',   icon: BookOpen },
  { id: 'assistant', label: 'Assistant', icon: Activity  },
  { id: 'archives',  label: 'Archives',  icon: Database  },
];

const MobileLayout = ({
  courseData,
  progress,
  activeItem,
  currentModule,
  currentMediators,
  resolveAsset,
  closeNarrator,
  showNarrator,
  toggleShowNarrator,
}) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { focusResource } = usePesaParcours();

  const [activeTab, setActiveTab] = useState('modules');
  const [narratorOpen, setNarratorOpen] = useState(false);

  // Bascule auto vers "contenu" quand un granule est sélectionné
  useEffect(() => {
    if (activeItem?.id) setActiveTab('contenu');
  }, [activeItem?.id]);

  // Sync état narrateur
  useEffect(() => {
    setNarratorOpen(showNarrator);
  }, [showNarrator]);

  const handleNarratorToggle = () => {
    toggleShowNarrator();
    setActiveTab('contenu'); // on reste sur contenu quand l'assistant s'ouvre
  };

  const bg = darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50';
  const headerBg = darkMode ? 'bg-black/60 border-white/5' : 'bg-white/90 border-slate-200';
  const tabBarBg = darkMode ? 'bg-black/80 border-white/5' : 'bg-white/95 border-slate-200';

  return (
    <div className={`h-screen w-screen flex flex-col overflow-hidden ${bg}`}>

      {/* ── GRID BG ── */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className={`absolute inset-0 ${
          darkMode
            ? 'bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)]'
            : 'bg-[linear-gradient(rgba(148,163,184,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.1)_1px,transparent_1px)]'
        }`} style={{ backgroundSize: '40px 40px' }} />
        <div className={`absolute top-0 left-0 w-64 h-64 ${darkMode ? 'bg-blue-600/5' : 'bg-blue-400/10'} blur-[80px] rounded-full`} />
        <div className={`absolute bottom-16 right-0 w-64 h-64 ${darkMode ? 'bg-orange-600/5' : 'bg-orange-400/10'} blur-[80px] rounded-full`} />
      </div>

      {/* ── HEADER MOBILE (56px) ── */}
      <header className={`h-14 shrink-0 flex items-center justify-between px-4 z-50 border-b backdrop-blur-xl ${headerBg}`}>

        {/* Retour */}
        <button
          onClick={() => navigate('/dashboard')}
          className={`p-2 rounded-xl border transition-all ${
            darkMode ? 'border-white/10 text-blue-400' : 'border-slate-300 text-blue-600'
          }`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>

        {/* Titre + Progress */}
        <div className="flex-1 mx-3 min-w-0">
          <p className={`text-[8px] font-mono uppercase tracking-[0.25em] truncate ${
            darkMode ? 'text-blue-400/60' : 'text-blue-600/70'
          }`}>
            {activeItem?.data?.titre
              ? `↳ ${activeItem.data.titre}`
              : 'Investigation_Active'}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <div className={`flex-1 h-1 rounded-full overflow-hidden ${darkMode ? 'bg-white/5' : 'bg-slate-200'}`}>
              <motion.div
                className="h-full bg-gradient-to-r from-blue-500 to-orange-500"
                initial={{ width: 0 }}
                animate={{ width: `${progress || 0}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
            <span className={`text-[9px] font-black font-mono shrink-0 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              {progress || 0}%
            </span>
          </div>
        </div>

        {/* Bouton assistant */}
        <button
          onClick={handleNarratorToggle}
          className={`p-2 rounded-xl border transition-all ${
            narratorOpen
              ? 'bg-blue-600 border-blue-500 text-white'
              : darkMode
                ? 'border-white/10 text-blue-400'
                : 'border-slate-300 text-blue-600'
          }`}
        >
          <Activity size={15} className={!narratorOpen ? 'animate-pulse' : ''} />
        </button>
      </header>

      {/* ── ZONE CONTENU (flex-1) ── */}
      <main className="flex-1 relative z-10 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="h-full w-full"
          >

            {/* TAB: MODULES */}
            {activeTab === 'modules' && (
              <div className={`h-full overflow-y-auto no-scrollbar ${
                darkMode ? 'bg-black/30' : 'bg-white/80'
              } backdrop-blur-xl`}>
                <div className="px-2 py-3">
                  <SidebarNavigation isCollapsed={false} />
                </div>
              </div>
            )}

            {/* TAB: CONTENU */}
            {activeTab === 'contenu' && (
              <div className={`h-full rounded-none overflow-hidden ${
                darkMode ? 'bg-black/30' : 'bg-white/80'
              } backdrop-blur-xl`}>
                <LearningArea />
              </div>
            )}

            {/* TAB: ASSISTANT — placeholder, le vrai est dans le drawer */}
            {activeTab === 'assistant' && (
              <div className="h-full flex flex-col items-center justify-center gap-4 px-6 text-center">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                  darkMode ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-200'
                }`}>
                  <Activity size={28} className={darkMode ? 'text-blue-400 animate-pulse' : 'text-blue-600 animate-pulse'} />
                </div>
                <div>
                  <p className={`text-sm font-black uppercase tracking-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    Neural Narrator
                  </p>
                  <p className={`text-[11px] mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                    {currentMediators?.length > 0
                      ? `${currentMediators.length} guide${currentMediators.length > 1 ? 's' : ''} disponible${currentMediators.length > 1 ? 's' : ''}`
                      : 'Aucun guide pour ce contenu'}
                  </p>
                </div>
                {currentMediators?.length > 0 && (
                  <button
                    onClick={() => setNarratorOpen(true)}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest"
                  >
                    Ouvrir le guide
                  </button>
                )}
              </div>
            )}

            {/* TAB: ARCHIVES */}
            {activeTab === 'archives' && (
              <div className="h-full overflow-y-auto no-scrollbar">
                <MobileResourceList
                  currentModule={currentModule}
                  darkMode={darkMode}
                />
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </main>

      {/* ── BOTTOM NAV (56px) ── */}
      <nav className={`h-14 shrink-0 flex items-stretch border-t z-50 backdrop-blur-xl ${tabBarBg}`}>
        {TABS.map(({ id, label, icon: Icon }) => {
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => {
                if (id === 'assistant') {
                  handleNarratorToggle();
                } else {
                  setActiveTab(id);
                }
              }}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-all ${
                isActive
                  ? darkMode ? 'text-blue-400' : 'text-blue-600'
                  : darkMode ? 'text-slate-600' : 'text-slate-400'
              }`}
            >
              <Icon size={18} className={isActive ? '' : ''} />
              <span className="text-[8px] font-black uppercase tracking-widest">{label}</span>
              {isActive && (
                <motion.div
                  layoutId="tab-indicator"
                  className={`absolute bottom-0 h-0.5 w-8 rounded-full ${darkMode ? 'bg-blue-400' : 'bg-blue-600'}`}
                />
              )}
            </button>
          );
        })}
      </nav>

      {/* ── NARRATOR DRAWER ── */}
      <MobileNarratorDrawer
        isOpen={narratorOpen}
        onClose={() => {
          setNarratorOpen(false);
          closeNarrator();
        }}
        mediators={currentMediators}
        resolveAsset={resolveAsset}
        darkMode={darkMode}
      />
      <AssetOverlay /> 
    </div>
  );
};

// ── Resource list inline pour mobile (sans le système width du ResourcePanel) ──
const MobileResourceList = ({ currentModule, darkMode }) => {
  const { openAsset, activeAsset, resolveAsset, setEmbeddedMedia } = usePesaParcours();
  const [showBonus, setShowBonus] = useState(false);

  const regularFiches = currentModule?.fiches || [];
  const bonusFiches   = currentModule?.bonusFiches || [];

  if (!regularFiches.length && !bonusFiches.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
        <Database size={28} className={darkMode ? 'text-slate-700' : 'text-slate-300'} />
        <p className={`text-[11px] font-mono uppercase tracking-widest ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
          Aucune archive disponible
        </p>
      </div>
    );
  }

  const AssetRow = ({ asset, accent = false, bonus = false }) => {
    const assetId = asset._id || asset.id;
    const isActive = (activeAsset?.id || activeAsset?._id) === assetId;    
    const Icon = asset.type === 'video' ? PlayCircle : FileText;

    const bg = bonus
      ? darkMode ? 'bg-amber-500/5 border-amber-500/20' : 'bg-amber-50 border-amber-200'
      : accent
        ? darkMode ? 'bg-blue-500/5 border-blue-500/20' : 'bg-blue-50 border-blue-200'
        : darkMode ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-100';

    return (
      <button
        onClick={() => openAsset(assetId)}
        className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border transition-all ${bg} ${
          isActive ? 'ring-1 ring-blue-500/50' : ''
        }`}
      >
        <Icon size={16} className={isActive
          ? 'text-blue-500'
          : bonus ? (darkMode ? 'text-amber-400' : 'text-amber-600')
          : darkMode ? 'text-slate-400' : 'text-slate-500'
        } />
        <span className={`flex-1 text-left text-[11px] font-medium truncate ${
          isActive
            ? 'text-blue-500 font-bold'
            : darkMode ? 'text-slate-300' : 'text-slate-700'
        }`}>
          {asset.titre}
        </span>
        {isActive && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />}
      </button>
    );
  };

  return (
    <div className="p-4 space-y-6">

      {regularFiches.length > 0 && (
        <div className="space-y-2">
          <p className={`text-[8px] font-black uppercase tracking-[0.3em] px-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            Documentation
          </p>
          <div className="space-y-2">
            {regularFiches.map(a => <AssetRow key={a.id || a._id} asset={a} />)}
          </div>
        </div>
      )}

      {bonusFiches.length > 0 && (
        <div className="space-y-2">
          <button
            onClick={() => setShowBonus(!showBonus)}
            className="flex items-center gap-2 px-1"
          >
            <span className={`text-[8px] font-black uppercase tracking-[0.3em] ${
              showBonus
                ? darkMode ? 'text-amber-400' : 'text-amber-600'
                : darkMode ? 'text-slate-500' : 'text-slate-400'
            }`}>
              Bonus_Files ({bonusFiches.length})
            </span>
            <svg
              width="10" height="10" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5"
              className={`transition-transform ${showBonus ? 'rotate-180' : ''} ${
                darkMode ? 'text-slate-500' : 'text-slate-400'
              }`}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <AnimatePresence>
            {showBonus && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden space-y-2"
              >
                {bonusFiches.map(a => <AssetRow key={a.id || a._id} asset={a} bonus />)}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default MobileLayout;